import type { TransferGateDenyCode } from '@/api/rentals';
import { useTranslation } from '@kgc/i18n';

/**
 * L1(b) Partner-46 — TRANSFER fizetési-mód UX-tükör (reaktív ág, (A)).
 *
 * A BE `TransferPaymentGateService` a valódi őr (money-write `PATCH /rentals/:id/payment`
 * ELŐTT fut, READ-only). Ez a komponens CSAK a gate-eredményt TÜKRÖZI a pultosnak a
 * fizetési-mód-választó mellett — 0 FE-számítás, a `code` a SSoT.
 *
 * Cserélhető forrás (McMaster ①-verdikt, „a FE-műved nem várakozik"):
 *   - (A) reaktív MOST: a `PATCH /payment` 400 `{ code, message }` válaszából (ApiError.code/message).
 *   - (B) proaktív KÉSŐBB: a READ-only `GET /rentals/:id/transfer-eligibility` ugyanezt a
 *     `{ code, reason }` alakot adja → változatlan propokkal átköthető, komponens-csere nélkül.
 *
 * Optional-guard: `code` nélkül nem renderel → a hívó merge-order-független.
 */
interface TransferEligibilityNoticeProps {
  /** A BE gate deny-kódja; null/undefined → nincs figyelmeztetés (nem renderel). */
  code: TransferGateDenyCode | null | undefined;
  /** A BE nyers `reason`-szövege — fallback ismeretlen/jövőbeli kódra (SSoT). */
  reason?: string | null | undefined;
}

export function TransferEligibilityNotice({ code, reason }: TransferEligibilityNoticeProps) {
  const { t } = useTranslation();

  if (!code) return null;

  const titleKey =
    code === 'TRANSFER_BANNED'
      ? 'rental.payment.transferBannedTitle'
      : code === 'TRANSFER_PERSON_BLOCKED'
        ? 'rental.payment.transferPersonBlockedTitle'
        : 'rental.payment.transferNotEligibleTitle';
  const bodyKey =
    code === 'TRANSFER_BANNED'
      ? 'rental.payment.transferBannedHint'
      : code === 'TRANSFER_PERSON_BLOCKED'
        ? 'rental.payment.transferPersonBlockedHint'
        : 'rental.payment.transferNotEligibleHint';

  // Ismeretlen kód (jövőbeli BE-bővítés — pl. a fenti (B) proaktív `GET /transfer-eligibility`
  // forrás, ami a `rentalTransferGateDenial` 3-kód-szűrője nélkül adhat új kódot): a lokalizált
  // szöveg helyett a nyers reason (SSoT). Szándékos forward-compat optional-guard, NEM dead code.
  const isKnown =
    code === 'TRANSFER_BANNED' ||
    code === 'TRANSFER_NOT_ELIGIBLE' ||
    code === 'TRANSFER_PERSON_BLOCKED';

  return (
    <div
      role="alert"
      aria-live="polite"
      data-testid="transfer-eligibility-notice"
      data-code={code}
      className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700/60 dark:bg-amber-900/20"
    >
      <p className="font-medium text-amber-800 dark:text-amber-200">
        {isKnown ? t(titleKey) : t('rental.payment.transferNotEligibleTitle')}
      </p>
      <p className="mt-1 text-amber-700 dark:text-amber-300">{isKnown ? t(bodyKey) : reason}</p>
      <p className="mt-2 font-medium text-amber-800 dark:text-amber-200">
        {t('rental.payment.transferCashHint')}
      </p>
    </div>
  );
}
